/**
 * FSRS-6 Wrapper um ts-fsrs.
 *
 * Pure functions — KEINE LocalStorage-Logik hier (siehe lib/store).
 * Tests in tests/fsrs.test.ts.
 */
import {
  createEmptyCard,
  FSRS,
  type Card as FsrsCard,
  type Grade,
  generatorParameters,
  Rating,
  type State,
} from 'ts-fsrs';
import type { Rating as ShkRating } from '$types/card';

export const DEFAULT_FSRS_PARAMS = generatorParameters({
  enable_fuzz: true,
  enable_short_term: true,
  /** Andys Ziel-Retention 90 % (Note 1 Anspruch). */
  request_retention: 0.9,
});

const _fsrs = new FSRS(DEFAULT_FSRS_PARAMS);

export function newCard(now: Date = new Date()): FsrsCard {
  return createEmptyCard(now);
}

const ratingMap: Record<ShkRating, Grade> = {
  1: Rating.Again,
  2: Rating.Hard,
  3: Rating.Good,
  4: Rating.Easy,
};

export interface ReviewResult {
  card: FsrsCard;
  /** Nächstes Review-Datum (UTC ms). */
  dueMs: number;
  state: State;
  /** Stability nach Review. */
  stability: number;
  /** Difficulty nach Review. */
  difficulty: number;
}

export function review(card: FsrsCard, rating: ShkRating, now = new Date()): ReviewResult {
  const fsrsRating = ratingMap[rating];
  const records = _fsrs.repeat(card, now);
  const log = records[fsrsRating];
  if (!log) throw new Error(`No record for rating ${rating}`);
  const next = log.card;
  return {
    card: next,
    dueMs: next.due.getTime(),
    state: next.state,
    stability: next.stability,
    difficulty: next.difficulty,
  };
}

/**
 * Vorschau der nächsten Fälligkeit (dueMs) je Rating OHNE den Review zu
 * committen — für echte „nächste Wiederholung"-Labels auf den Rating-Buttons.
 */
export function previewIntervals(card: FsrsCard, now = new Date()): Record<ShkRating, number> {
  const records = _fsrs.repeat(card, now);
  return {
    1: records[Rating.Again].card.due.getTime(),
    2: records[Rating.Hard].card.due.getTime(),
    3: records[Rating.Good].card.due.getTime(),
    4: records[Rating.Easy].card.due.getTime(),
  };
}

export function isDue(card: FsrsCard, now = new Date()): boolean {
  return card.due.getTime() <= now.getTime();
}

export function dueDelta(card: FsrsCard, now = new Date()): number {
  return card.due.getTime() - now.getTime();
}

/** Default-Sortier-Funktion: überfaellige zuerst, dann nach due-Date aufsteigend. */
export function compareDue(a: FsrsCard, b: FsrsCard): number {
  return a.due.getTime() - b.due.getTime();
}

export type { Card as FsrsCard } from 'ts-fsrs';
export { Rating, State } from 'ts-fsrs';
